import React from 'react'

// @src imports
import {
  CardType,
  ImageFragment,
  SendableCardWithTemplateFragment,
} from 'src/graphql'

// relative imports
import {
  ComputedFullBleed,
  ComputedPanel,
  frontImage as getFrontImage,
  insideRightImage as getInsideRightImage,
  selectedVariationId as getSelectedVariationId,
  variations as getVariations,
} from '../../../redux/selectors/editor'
import ImageElement from '../../components/ImageElement'
import PostCardSleeve from '../../components/PostCardSleeve'
import VariationToggle from '../../components/VariationToggle/VariationToggle'
import TextElement from '../../components/TextElement'
import PrintBarCode from '../../components/PrintBarCode/PrintBarCode'

import styles from './styles'
import { EditorImage } from '../../EditorCard'
import * as Utils from '../../utils'
import { Api } from '../../api'

type PanelProps = {
  panel: ComputedPanel
  hasBackgroundSpread?: boolean
  index: number
  api: Api
  fullBleed: ComputedFullBleed
}

const Panel: React.FC<PanelProps> = props => {
  const { api, panel, hasBackgroundSpread, index, fullBleed } = props
  const { selectPanel, step, fullBleeds, updateCard, card } = api

  const variations = getVariations(card)
  const selectedVariationId = getSelectedVariationId(card, fullBleeds)

  const handleSelectVariation = async (
    variation: SendableCardWithTemplateFragment,
  ) => {
    const editorImage = (image: ImageFragment): EditorImage => ({
      image: {
        id: image.id,
        width: image.width,
        height: image.height,
        url: image.url,
        smallThumb: image.smallThumb,
      },
      position: { x: 0.5, y: 0.5 },
      scale: 1,
      filter: null,
    })

    const frontImage = getFrontImage(variation)
    const insideRightImage = getInsideRightImage(variation)
    await updateCard(
      card,
      fullBleeds.map((fullBleed, fullBleedLocation) => ({
        ...fullBleed,
        panels: fullBleed.panels.map((panel, panelLocation) => ({
          ...panel,
          elements: panel.elements.map(element => ({
            ...element,
            image: element.image
              ? fullBleedLocation === 0 &&
                panelLocation === 0 &&
                element.locked &&
                frontImage
                ? editorImage(frontImage)
                : insideRightImage &&
                  Utils.isInsideRightPanel(
                    card.type,
                    fullBleedLocation,
                    panelLocation,
                  ) &&
                  element.locked
                ? editorImage(insideRightImage)
                : element.image
              : element.image,
          })),
        })),
      })),
    )
  }

  const handleSelect = () => selectPanel(step.fullBleedIndex, index)

  const orientation = card.isHorizontal ? 'LANDSCAPE' : 'PORTRAIT'
  return (
    <div
      id={`${panel.name}_panel`}
      key={panel.location}
      css={() => ({
        ...styles.container(
          panel.isSelected,
          card.type,
          panel.name,
          orientation,
        ),
        ...styles.panelBackground(!!hasBackgroundSpread, panel.backgroundColor),
      })}
      onClick={!panel.isCustom ? () => handleSelect() : undefined}
    >
      <div css={styles.panelContent}>
        {/* Loop through panel elements and initialize editor components */}
        {panel.elements.map(element => {
          const elementDisplayStyles: React.CSSProperties = {
            position: 'absolute',
            top: Math.max(element.y * 100, 0) + '%',
            left: Math.max(element.x * 100, 0) + '%',
            width: Math.min(element.width * 100, 100) + '%',
            height: Math.min(element.height * 100, 100) + '%',
            transform:
              element.rotation !== 0
                ? `rotate(${element.rotation}deg)`
                : 'none',
            transformOrigin: '0% 0%',
          }
          return (
            <div key={element.location}>
              {element.image && (
                <ImageElement
                  key={element.location}
                  api={api}
                  panel={panel}
                  element={element}
                  elementDisplayStyles={elementDisplayStyles}
                />
              )}
              {element.text && (
                <TextElement
                  api={api}
                  key={element.location}
                  panel={panel}
                  fullBleed={fullBleed}
                  element={element}
                  elementText={element.text}
                  elementDisplayStyles={elementDisplayStyles}
                />
              )}
              {variations && element.locked && panel.name !== 'Back' && (
                <VariationToggle
                  variations={variations}
                  selectedVariationId={selectedVariationId}
                  selectVariation={handleSelectVariation}
                />
              )}
            </div>
          )
        })}
      </div>
      {card.type === CardType.POSTCARD && panel.name === 'Back' && (
        <PostCardSleeve />
      )}
      {panel.name === 'Back' && card.type !== CardType.POSTCARD && (
        <PrintBarCode
          style={
            {
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            } as React.CSSProperties
          }
        />
      )}
    </div>
  )
}

export default Panel
